/**
 * Custom hook pour la gestion des devis
 * ✅ SKILL FACTURXPRO: Architecture 3 couches
 *
 * Component (QuotesListResponsive.tsx)
 *    ↓
 * Custom Hook (useQuotesManagement) ← VOUS ÊTES ICI
 *    ↓
 * API Layer (quotesAPI, invoicesAPI, quoteRemindersAPI)
 *    ↓
 * Backend
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { quotesAPI, invoicesAPI, quoteRemindersAPI } from '../utils/api';
import { Quote as BaseQuote } from '../types/models';
import { notify } from '../stores/notificationStore';
import { logger } from '../utils/logger';

// Extended Quote type with relations (as returned by API with include)
interface Quote extends BaseQuote {
  Client?: {
    fullName?: string;
    companyName?: string;
    email?: string;
  };
}

interface UseQuotesManagementReturn {
  // Data
  quotes: Quote[];
  filteredQuotes: Quote[];

  // UI State
  loading: boolean;
  error: string | null;
  activeMenu: string | null;

  // Filter State
  searchQuery: string;
  activeStatuses: string[];
  dateFrom: string;
  dateTo: string;

  // Actions - CRUD
  fetchQuotes: () => Promise<void>;
  createQuote: (data: Partial<Quote>) => Promise<Quote | null>;
  updateQuote: (id: string, data: any) => Promise<void>;
  deleteQuote: (id: string) => Promise<void>;
  duplicateQuote: (id: string) => Promise<Quote | null>;

  // Actions - Status
  updateQuoteStatus: (id: string, newStatus: string) => Promise<void>;
  getAllowedStatusTransitions: (currentStatus: string) => string[];
  getStatusOptions: (currentStatus: string) => Array<{ value: string; label: string }>;
  getStatusConfig: (status: string) => { label: string; variant: string; icon: any };

  // Actions - Related
  createInvoiceFromQuote: (quoteId: string) => Promise<any>;
  sendReminderForQuote: (quoteId: string | number) => Promise<void>;

  // Actions - UI
  setActiveMenu: (id: string | null) => void;
  setSearchQuery: (query: string) => void;
  setActiveStatuses: (statuses: string[]) => void;
  setDateFrom: (date: string) => void;
  setDateTo: (date: string) => void;
  clearError: () => void;

  // Computed
  formatDate: (dateStr: string) => string;
  formatCurrency: (amount: number) => string;
}

export function useQuotesManagement(): UseQuotesManagementReturn {
  // ==================== STATE ====================

  // Data state
  const [quotes, setQuotes] = useState<Quote[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [activeStatuses, setActiveStatuses] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // ==================== DATA LOADING ====================

  /**
   * Charger les devis
   */
  const fetchQuotes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await quotesAPI.getAll();
      setQuotes(Array.isArray(data) ? data : []);
    } catch (err: any) {
      const errorMessage = err.message || 'Erreur lors du chargement des devis';
      setError(errorMessage);
      logger.error('fetchQuotes error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ==================== CRUD OPERATIONS ====================

  /**
   * Créer un devis
   */
  const createQuote = useCallback(async (data: Partial<Quote>) => {
    try {
      setLoading(true);
      const newQuote = await quotesAPI.create(data);
      notify.success('Devis créé avec succès', 'Succès');
      await fetchQuotes();
      return newQuote;
    } catch (err: any) {
      logger.error('createQuote error:', err);
      notify.error(err.message || 'Erreur lors de la création du devis', 'Erreur');
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchQuotes]);

  /**
   * Mettre à jour un devis
   */
  const updateQuote = useCallback(async (id: string, data: any) => {
    try {
      setLoading(true);
      await quotesAPI.update(id, data);
      notify.success('Devis mis à jour avec succès', 'Succès');
      await fetchQuotes();
    } catch (err: any) {
      logger.error('updateQuote error:', err);
      notify.error(err.message || 'Erreur lors de la mise à jour du devis', 'Erreur');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchQuotes]);

  /**
   * Supprimer un devis
   */
  const deleteQuote = useCallback(async (id: string) => {
    try {
      setLoading(true);
      await quotesAPI.delete(id);
      notify.success('Devis supprimé avec succès', 'Suppression réussie');
      await fetchQuotes();
    } catch (err: any) {
      logger.error('deleteQuote error:', err);
      notify.error(err.message || 'Erreur lors de la suppression', 'Erreur');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchQuotes]);

  /**
   * Dupliquer un devis
   */
  const duplicateQuote = useCallback(async (id: string) => {
    try {
      setLoading(true);
      const newQuote = await quotesAPI.duplicate(id);
      notify.success('Devis dupliqué avec succès', 'Duplication réussie');
      await fetchQuotes();
      return newQuote;
    } catch (err: any) {
      logger.error('duplicateQuote error:', err);
      notify.error(err.message || 'Erreur lors de la duplication', 'Erreur');
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchQuotes]);

  // ==================== STATUS MANAGEMENT ====================

  /**
   * Mettre à jour le statut d'un devis
   */
  const updateQuoteStatus = useCallback(async (id: string, newStatus: string) => {
    try {
      setLoading(true);
      await quotesAPI.updateStatus(id, newStatus);
      notify.success('Statut mis à jour', 'Mise à jour réussie');
      await fetchQuotes();
    } catch (err: any) {
      logger.error('updateQuoteStatus error:', err);
      notify.error(err.message || 'Erreur lors de la mise à jour', 'Erreur');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchQuotes]);

  /**
   * Obtenir les transitions de statut autorisées
   * ✅ CONFORME au modèle Prisma EstimateStatus (UPPERCASE uniquement)
   */
  const getAllowedStatusTransitions = useCallback((currentStatus: string): string[] => {
    const transitions: Record<string, string[]> = {
      DRAFT: ['SENT'],
      SENT: ['ACCEPTED', 'REJECTED', 'EXPIRED'],
      ACCEPTED: [],
      REJECTED: [],
      EXPIRED: [],
    };
    return transitions[currentStatus.toUpperCase()] || [];
  }, []);

  /**
   * Obtenir les options de statut disponibles
   * ✅ CONFORME au modèle Prisma EstimateStatus (UPPERCASE uniquement)
   */
  const getStatusOptions = useCallback((currentStatus: string) => {
    const allStatuses = [
      { value: 'DRAFT', label: 'Brouillon' },
      { value: 'SENT', label: 'Envoyé' },
      { value: 'ACCEPTED', label: 'Validé' },
      { value: 'REJECTED', label: 'Refusé' },
      { value: 'EXPIRED', label: 'Expiré' },
    ];

    const allowedTransitions = getAllowedStatusTransitions(currentStatus);

    return allStatuses.filter(
      status => status.value === currentStatus.toUpperCase() || allowedTransitions.includes(status.value)
    );
  }, [getAllowedStatusTransitions]);

  /**
   * Obtenir la configuration du statut (label, variant, icon)
   * ✅ CONFORME au modèle Prisma EstimateStatus (UPPERCASE uniquement)
   */
  const getStatusConfig = useCallback((status: string) => {
    const configs: Record<string, { label: string; variant: string; icon: any }> = {
      DRAFT: { label: 'Brouillon', variant: 'pending', icon: null },
      SENT: { label: 'Envoyé', variant: 'info', icon: null },
      ACCEPTED: { label: 'Validé', variant: 'success', icon: null },
      REJECTED: { label: 'Refusé', variant: 'alert', icon: null },
      EXPIRED: { label: 'Expiré', variant: 'warning', icon: null },
    };
    return configs[status.toUpperCase() as keyof typeof configs] || configs.DRAFT;
  }, []);

  // ==================== RELATED OPERATIONS ====================

  /**
   * Créer une facture à partir d'un devis
   */
  const createInvoiceFromQuote = useCallback(async (quoteId: string) => {
    try {
      setLoading(true);
      const result = await invoicesAPI.createFromQuote(quoteId);
      notify.success('Facture créée avec succès !', 'Création réussie');
      await fetchQuotes(); // Refresh quotes list
      return result;
    } catch (err: any) {
      logger.error('createInvoiceFromQuote error:', err);
      notify.error(err.message || 'Erreur lors de la création', 'Erreur');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchQuotes]);

  /**
   * Envoyer une relance pour un devis
   */
  const sendReminderForQuote = useCallback(async (quoteId: string | number) => {
    try {
      const id = typeof quoteId === 'string' ? parseInt(quoteId, 10) : quoteId;
      await quoteRemindersAPI.sendManual(id);
      notify.success('Relance envoyée avec succès', 'Email envoyé');
    } catch (err: any) {
      logger.error('sendReminderForQuote error:', err);
      notify.error(err.message || 'Erreur lors de l\'envoi de la relance', 'Erreur d\'envoi');
      throw err;
    }
  }, []);

  // ==================== HELPER FUNCTIONS ====================

  /**
   * Formater une date en français
   */
  const formatDate = useCallback((dateStr: string) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return new Intl.DateTimeFormat('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(date);
    } catch {
      return '-';
    }
  }, []);

  /**
   * Formater un montant en euros
   */
  const formatCurrency = useCallback((amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount || 0);
  }, []);

  /**
   * Nettoyer l'erreur
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // ==================== FILTERING ====================

  /**
   * Filtrer les devis selon les critères
   */
  const filteredQuotes = useMemo(() => {
    return quotes.filter((quote) => {
      // Filtre par recherche (numéro, client, montant)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesNumber = quote.number?.toLowerCase().includes(query);
        const matchesClient =
          quote.Client?.fullName?.toLowerCase().includes(query) ||
          quote.Client?.companyName?.toLowerCase().includes(query);
        const matchesAmount = quote.totalTTC?.toString().includes(query);

        if (!matchesNumber && !matchesClient && !matchesAmount) {
          return false;
        }
      }

      // Filtre par statuts actifs
      // ✅ quote.status vient de la DB (UPPERCASE), activeStatuses vient de l'UI (UPPERCASE)
      if (activeStatuses.length > 0) {
        if (!activeStatuses.includes(quote.status.toUpperCase())) {
          return false;
        }
      }

      // Filtre par date (depuis)
      if (dateFrom) {
        const quoteDate = new Date(quote.createdAt);
        const fromDate = new Date(dateFrom);
        if (quoteDate < fromDate) {
          return false;
        }
      }

      // Filtre par date (jusqu'à)
      if (dateTo) {
        const quoteDate = new Date(quote.createdAt);
        const toDate = new Date(dateTo);
        if (quoteDate > toDate) {
          return false;
        }
      }

      return true;
    });
  }, [quotes, searchQuery, activeStatuses, dateFrom, dateTo]);

  // ==================== EFFECTS ====================

  // Charger les devis au montage
  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  // ==================== RETURN ====================

  return {
    // Data
    quotes,
    filteredQuotes,

    // UI State
    loading,
    error,
    activeMenu,

    // Filter State
    searchQuery,
    activeStatuses,
    dateFrom,
    dateTo,

    // Actions - CRUD
    fetchQuotes,
    createQuote,
    updateQuote,
    deleteQuote,
    duplicateQuote,

    // Actions - Status
    updateQuoteStatus,
    getAllowedStatusTransitions,
    getStatusOptions,
    getStatusConfig,

    // Actions - Related
    createInvoiceFromQuote,
    sendReminderForQuote,

    // Actions - UI
    setActiveMenu,
    setSearchQuery,
    setActiveStatuses,
    setDateFrom,
    setDateTo,
    clearError,

    // Computed
    formatDate,
    formatCurrency,
  };
}
